import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import { Label } from '@connected/form-fields/components/label';
import { Button } from '@connected/ui/components/button';
import { Input } from '@connected/ui/components/input';
import {
    Popout,
    PopoutBody,
    PopoutContent,
    PopoutFooter,
    PopoutHeader,
    PopoutTitle,
    PopoutTrigger,
} from '@connected/ui/components/popout';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAvailableChecklists } from '@/hooks/queries/use-checklist';
import { useFormTouched } from '@/hooks/use-form-touched';
import { logger } from '@/lib/logger';
import { showConfirm } from '@/stores/confirmer';

import { useCreateChecklistItemMutation, useCreateFinalCircuitMutation } from '../hooks/use-create-final-circuit';

type FinalCircuitFormValues = {
    identifier: string;
};

export type FinalCircuitCreateModalProps = {
    electricalProjectVersionId: string;
    trigger?: React.ReactNode;
    onCreated: (finalCircuitId: string) => void;
};

export function FinalCircuitCreateModal({
    electricalProjectVersionId,
    trigger,
    onCreated,
}: FinalCircuitCreateModalProps) {
    const { t } = useTranslation('re-inspections');
    const { t: c } = useTranslation('common');

    const [isOpen, setIsOpen] = useState(false);
    const [newFinalCircuitId, setNewFinalCircuitId] = useState<string>(() => crypto.randomUUID());

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        // Reset the new final circuit ID when the modal is opened
        // This ensures that a new ID is generated each time the modal is opened
        const newId = crypto.randomUUID();
        logger.debug(['Resetting new final circuit ID', newId]);

        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setNewFinalCircuitId(newId);
    }, [isOpen]);

    const { data } = useAvailableChecklists({
        electricalProjectVersionId,
        kind: 'FinalCircuit',
    });

    const checklistItems = data.items?.map(item => ({
        ElectricalCheckListItemId: item.ElectricalCheckListItemId!,
        Description: item.Description!,
    })) ?? [];

    const createChecklistItemMutation = useCreateChecklistItemMutation({
        electricalProjectVersionId,
        checklistItems,
    });

    const createFinalCircuitMutation = useCreateFinalCircuitMutation({
        electricalProjectVersionId,
        onSuccess: (finalCircuitId: string) => {
            onCreated(finalCircuitId);
            setIsOpen(false);
        },
    });

    const defaultValues: FinalCircuitFormValues = {
        identifier: '',
    };

    const { Field, handleSubmit, reset, store } = useForm<
        FinalCircuitFormValues,
        ZodValidator
    >({
        defaultValues,
        onSubmit: ({ value }) => {
            createFinalCircuitMutation.mutate({
                body: {
                    ElectricalProjectVersionId: electricalProjectVersionId,
                    Order: 9999,
                    Identifier: value.identifier,
                    SkipCreateCheckListItems: true,
                    ElectricalProjectFinalCircuitId: newFinalCircuitId,
                },
            });

            data.items?.forEach((checkList) => {
                createChecklistItemMutation.mutate({
                    body: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ObjectId: newFinalCircuitId,
                        ElectricalCheckListItemId: checkList.ElectricalCheckListItemId!,
                        ObjectKind: 'FinalCircuit',
                        ElectricalProjectCheckListItemId: crypto.randomUUID(),
                    },
                });
            });
        },
        validatorAdapter: zodValidator(),
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm(c('messages.confirmClose'))) {
                setIsOpen(false);
                reset();
                createFinalCircuitMutation.reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
            createFinalCircuitMutation.reset();
        }
    }

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            {trigger && (
                <PopoutTrigger asChild>
                    {trigger}
                </PopoutTrigger>
            )}
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>
                        {t('installation.underlying-installations.modal.title.label')}
                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form
                        onSubmit={onSubmit}
                        className="flex flex-col gap-4 py-6"
                    >
                        <Field
                            name="identifier"
                            validators={{
                                onChange:
                                    validators.createElectricalProjectFinalCircuitCommand.identifier,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t('finalCircuits.form.name.label')}
                                >
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('finalCircuits.form.name.placeholder')}
                                    />
                                </Label>
                            )}
                        </Field>

                    </form>
                </PopoutBody>
                <PopoutFooter>
                    <Button
                        className="w-full"
                        onClick={handleClose}
                        variant="outline"
                        disabled={createFinalCircuitMutation.isPending}
                    >
                        {c('actions.general.cancel_short')}
                    </Button>
                    <Button
                        className="w-full"
                        onClick={handleSubmit}
                        disabled={createFinalCircuitMutation.isPending}
                    >
                        {c('actions.general.save')}
                    </Button>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}
